import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "../../environments/environment";

export interface NotificationStatusDetail {
  id: number;
  name: string;
  enabled: boolean;
}

export interface NotificationStatusResponse {
  enabled: boolean;
  details: NotificationStatusDetail[];
}

/** Un aviso de la campanita. El feed se deriva en el backend, no hay tabla de notificaciones. */
export interface UserNotification {
  kind: 'requirement_assigned' | 'contract_alert';
  refid: number;
  title: string;
  text: string;
  date: string;
  link: string;
  unread: boolean;
}

export interface UserNotificationsResponse {
  notifications: UserNotification[];
  unreadCount: number;
  lastSeenAt: string | null;
}

@Injectable({
  providedIn: "root",
})
export class NotificationsService {
  constructor(private http: HttpClient) {}

  /**
   * Obtiene el estado de las notificaciones de emails para tipos de requerimiento
   * @returns Estado global y detallado de notificaciones por tipo de requerimiento
   */
  async getNotificationsStatus(): Promise<NotificationStatusResponse> {
    const resp: any = await this.http
      .get<any>(
        `${environment.apiUrl}/trackvision/requirementtype/notifications/status`,
        {
          headers: new HttpHeaders().set(
            "Token",
            localStorage.getItem("access_token") || ""
          ),
        }
      )
      .toPromise();
    // Extraer response.result si existe, de lo contrario usar la respuesta directamente
    return resp?.result || resp;
  }

  /**
   * Actualiza la configuración de notificaciones
   * @param enabled true para activar, false para desactivar
   * @param allTypes si es true, se aplica a todos los tipos de requerimiento
   * @param requirementTypeIds IDs específicos de tipos de requerimiento (opcional)
   */
  async updateNotificationsConfig(
    enabled: boolean,
    allTypes: boolean = true,
    requirementTypeIds?: number[]
  ): Promise<any> {
    const payload: any = {
      enabled,
      allTypes,
    };

    if (requirementTypeIds && requirementTypeIds.length > 0) {
      payload.requirementTypeIds = requirementTypeIds;
    }

    const resp = await this.http
      .post(`${environment.apiUrl}/trackvision/requirementtype/notifications/config/update`, payload, {
        headers: new HttpHeaders().set(
          "Token",
          localStorage.getItem("access_token") || ""
        ),
      })
      .toPromise();
    return resp;
  }

  /**
   * Feed de la campanita: lo que le concierne al usuario y cuántas cosas nuevas hay.
   *
   * OJO: esto NO es lo mismo que `getNotificationsStatus`, que es el interruptor de
   * correos del perfil. Comparten la palabra "notificaciones" y no tienen nada que
   * ver — uno decide si te llegan mails, este muestra tu bandeja.
   */
  async getUserNotifications(limit = 20): Promise<UserNotificationsResponse> {
    const resp: any = await this.http
      .get<any>(`${environment.apiUrl}/trackvision/notification/list?limit=${limit}`, {
        headers: new HttpHeaders().set(
          "Token",
          localStorage.getItem("access_token") || ""
        ),
      })
      .toPromise();
    const data = resp?.result || resp;
    return {
      notifications: data?.notifications || [],
      unreadCount: data?.unreadCount || 0,
      lastSeenAt: data?.lastSeenAt || null,
    };
  }

  /** Marca la campanita como vista: apaga el contador hasta que llegue algo nuevo. */
  async markNotificationsSeen(): Promise<void> {
    await this.http
      .post(`${environment.apiUrl}/trackvision/notification/seen`, {}, {
        headers: new HttpHeaders().set(
          "Token",
          localStorage.getItem("access_token") || ""
        ),
      })
      .toPromise();
  }
}
